import { useLocation, useNavigate } from 'react-router-dom';
import FaIcon from '@/components/common/FaIcon';
import { hapticTap } from '@/utils/haptics';

interface NavTab {
  key: string;
  icon: string;
  label: string;
  path: string | null;
}

const TABS: NavTab[] = [
  { key: 'dashboard', icon: 'gauge-high', label: 'Dashboard', path: '/' },
  { key: 'map', icon: 'map-location-dot', label: 'Map', path: '/map' },
  { key: 'incidents', icon: 'triangle-exclamation', label: 'Incidents', path: '/incidents' },
  { key: 'tasks', icon: 'list-check', label: 'Tasks', path: '/tasks' },
  { key: 'menu', icon: 'bars', label: 'Menu', path: null },
];

interface MobileBottomNavProps {
  onMenuPress: () => void;
}

export default function MobileBottomNav({ onMenuPress }: MobileBottomNavProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string | null) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleTabPress = (tab: NavTab) => {
    hapticTap();
    if (tab.path === null) {
      onMenuPress();
    } else {
      navigate(tab.path);
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-surface-elevated border-t border-gray-200 dark:border-gray-800 lg:hidden"
      style={{ paddingBottom: 'var(--safe-area-bottom, env(safe-area-inset-bottom, 0px))' }}
      aria-label="Main navigation"
    >
      <div className="flex items-stretch justify-around h-16">
        {TABS.map((tab) => {
          const active = isActive(tab.path);
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabPress(tab)}
              className={`relative flex flex-col items-center justify-center flex-1 min-w-[44px] min-h-[44px] px-1 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--interactive-focus-ring)] ${
                active
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-theme-tertiary'
              }`}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
            >
              <FaIcon icon={tab.icon} className={`h-5 w-5 ${active ? 'scale-110' : ''} transition-transform duration-150`} />
              <span className={`text-[10px] mt-1 font-medium leading-none ${active ? 'font-semibold' : ''}`}>
                {tab.label}
              </span>
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
